import React from "react";
import { CheckRounded, CloseRounded } from "@mui/icons-material";

/**
 * A list-toolbar filter, drawn as a pill rather than a checkbox.
 *
 * A checkbox in a toolbar reads as a form field somebody has to fill in, and it
 * carries none of the page's own language — the summary tiles above these lists
 * are already "press it and it turns primary-tinted", so a filter that sits two
 * inches below them should answer to the same gesture and the same colours.
 *
 * Two shapes, one component:
 *
 *   onClick  — a toggle. `active` tints it and adds a tick, so on/off is legible
 *              at a glance across the room, not just from the state of a 13px box.
 *   count    — optional, and independent of the two shapes. A muted pill after the
 *              label saying how much is behind this filter. The label truncates
 *              around it; the number does not.
 *   compact  — 26px instead of 30px, for a row carrying dozens of these rather
 *              than a toolbar carrying one. Type size is unchanged.
 *   solid    — the selected chip is FILLED with the brand colour rather than
 *              tinted. For a long single-select row, where the one that is on has
 *              to be findable at a glance among a hundred that are not.
 *   onClear  — an applied filter. The chip names what is being filtered and the ×
 *              drops it, which tells a reader why the list is short. `onClear` is
 *              a nested <button>, so the chip itself is a <span> in this shape —
 *              a button inside a button is invalid and Safari swallows the inner
 *              click.
 */
const FilterChip = ({
  label,
  icon,
  count,
  active = false,
  compact = false,
  solid = false,
  onClick,
  onClear,
  clearTitle,
  title,
  disabled = false,
  className = "",
}) => {
  // Two densities, because a toolbar carrying one or two of these and a strip
  // carrying a hundred and sixty-six are not the same problem. Compact takes the
  // chip from 30px to 26px and about 10px off its width — which is 10px × 166 of
  // scrolling on smoon_db — while leaving the type size alone. Shrinking the
  // CONTROL is the win; shrinking the text would just make it harder to read.
  //
  // A prop rather than a caller-supplied className: padding passed in through
  // className collides with the padding set here, and which one wins depends on
  // Tailwind's output order rather than on the order they are written.
  const pad = compact ? "py-[3px]" : "py-[5px]";
  const gap = compact ? "gap-1" : "gap-1.5";

  // Leading and trailing padding, always as ps/pe rather than px, so the two can
  // differ without the collision the comment below warns about.
  const ps = compact ? "ps-[9px]" : "ps-3";
  // A chip ending in the count pill takes the SAME trailing inset the clear shape
  // gives its × — the pill is a rounded object at the edge, and a full text
  // padding after it leaves it stranded in the middle of the chip instead of
  // sitting evenly inside it. 4px here matches the ~4px the pill already has
  // above and below it, so its gap to the border is equal on all three sides.
  const pe = count != null ? "pe-1" : compact ? "pe-[9px]" : "pe-3";

  // No horizontal padding here: each shape sets its own, because px-3 and ps-3/pe-1
  // are different properties and which one wins depends on Tailwind's output order,
  // not on the order they are written in this string.
  const base =
    `inline-flex items-center ${gap} rounded-full border ${pad} text-[12px] leading-[18px] whitespace-nowrap select-none transition-colors`;
  // `solid` fills the selected chip with the brand colour instead of tinting it.
  // Opt-in rather than the default: the tinted form is what a toolbar filter wants,
  // where the chip sits among other controls and a block of primary would shout.
  // A single-select row of a hundred chips is the opposite problem — one of them is
  // the answer, and it should be findable without reading any of the others.
  const tone = active
    ? solid
      ? "border-primary bg-primary text-white font-semibold"
      : "border-primary-300 bg-primary-50 text-primary font-semibold"
    : "border-gray-200 bg-white text-slate-600 font-medium";

  const body = (
    <>
      {icon && <span className="flex items-center shrink-0">{icon}</span>}
      {/* min-w-0 is explicit rather than relying on `truncate`'s overflow:hidden
          collapsing min-width:auto — that works, but it stops being obvious once
          the count gives the label a sibling to share the width with. */}
      <span className="truncate min-w-0">{label}</span>
      {count != null && (
        // shrink-0 IS the contract of this pill: the name truncates, the number
        // never does. Without it flex shrinks both and the digits clip, which is
        // the one thing a count must not do.
        //
        // Neutral on a tinted chip, on purpose: a primary-100 fill would disappear
        // into the chip on hover, because an ACTIVE tinted chip's hover is itself
        // bg-primary-100 (below). The count is metadata; the tint, border and tick
        // already carry the selection.
        //
        // On a SOLID chip it has to invert, or a slate pill with slate digits sits
        // as a muddy patch on the brand colour. White with primary digits keeps the
        // same "light plate, coloured number" reading as the inactive chip, and it
        // is the highest-contrast option on a filled background.
        //
        // No vertical padding: the chip's content box is 20px (18px compact) and
        // leading-[16px] sits inside either. A pill taller than the box would give
        // the row a vertical overflow, and the global 10px scrollbar would try to
        // paint it — which is why this stays a line-height rather than padding.
        <span
          className={`shrink-0 rounded-full px-[6px] text-[11px] font-semibold
            leading-[16px] tabular-nums ${
              active && solid
                ? "bg-white/90 text-primary"
                : "bg-slate-100 text-slate-500"
            }`}
        >
          {(count || 0).toLocaleString()}
        </span>
      )}
    </>
  );

  if (onClear) {
    return (
      <span
        className={`${base} ${tone} ${ps} pe-1 ${className}`}
        title={title}
      >
        {body}
        <button
          type="button"
          onClick={onClear}
          title={clearTitle}
          aria-label={clearTitle || label}
          className="flex items-center rounded-full p-[2px] text-current opacity-70 hover:opacity-100 hover:bg-black/5"
        >
          <CloseRounded className="!text-[14px]" />
        </button>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      title={title}
      className={`${base} ${tone} ${ps} ${pe} ${
        disabled
          ? "opacity-50 cursor-not-allowed"
          : active
            ? // Deepen the fill on hover. Reaching for the usual hover:border-primary-200
              // here would LIGHTEN the ring of a chip that is already on — and on a
              // solid chip primary-100 would wash it out completely, so it steps down
              // the scale instead.
              solid
              ? "cursor-pointer hover:bg-primary-600"
              : "cursor-pointer hover:bg-primary-100"
            : "cursor-pointer hover:border-primary-200 hover:text-primary"
      } ${className}`}
    >
      {body}
      {active && <CheckRounded className="!text-[14px] shrink-0" />}
    </button>
  );
};

export default FilterChip;
